import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CreateGalleryThemeBodyDto,
  GalleryTheme,
  GalleryThemeDto,
  Typography,
  UpdateGalleryBodyDto,
} from '@nusign/picflow-backend-types';
import { catchError, forkJoin, map, Observable, of, take, tap } from 'rxjs';
import { isDarkColorFromString } from '../lib/is-dark-color';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { GoogleFontService } from './google-font.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';

export interface StandardGalleryTheme {
  category: string;
  theme: GalleryTheme;
}

@Injectable({ providedIn: 'root' })
export class GalleryThemeService {
  private config = inject(PICFLOW_CONFIG);
  private permissionService = inject(PermissionService);
  private http = inject(HttpClient);
  private loader = inject(ObservableDataLoaderService);
  private googleFontService = inject(GoogleFontService);

  public getStandardThemes(): Observable<StandardGalleryTheme[]> {
    return this.http.get<{ data: StandardGalleryTheme[] }>(`${this.config.apiEndpoint}/gallery_themes/standard`).pipe(
      map(resp => resp.data),
      catchError(() => {
        return of([]);
      })
    );
  }

  public getCustomThemes(): Observable<GalleryThemeDto[]> {
    if (!this.permissionService.canDisplayElement('gallery:themes')) {
      return of([]);
    }
    return this.loader.get('custom-gallery-themes', () => {
      return this.http
        .get<{
          data: GalleryThemeDto[];
        }>(`${this.config.apiEndpoint}/gallery_themes`, { context: new HttpContext().set(IGNORE_AUTH_ERRORS, true) })
        .pipe(
          catchError(() => of({ data: [] })),
          map(resp => resp.data)
        );
    });
  }

  public createCustomTheme(params: CreateGalleryThemeBodyDto) {
    return this.http.post<GalleryThemeDto>(`${this.config.apiEndpoint}/gallery_themes`, params).pipe(
      tap(res => {
        this.loader.update<GalleryThemeDto[]>('custom-gallery-themes', themes => [...themes, res]);
      })
    );
  }

  public updateCustomTheme(id: string, params: UpdateGalleryBodyDto) {
    return this.http.patch<GalleryThemeDto>(`${this.config.apiEndpoint}/gallery_themes/${id}`, params).pipe(
      tap(theme => {
        this.loader.update<GalleryThemeDto[]>('custom-gallery-themes', themes => {
          return themes.map(t => (t.id === id ? theme : t));
        });
      })
    );
  }

  public deleteCustomTheme(id: string) {
    return this.http.delete(`${this.config.apiEndpoint}/gallery_themes/${id}`).pipe(
      tap(() => {
        this.loader.update<GalleryThemeDto[]>('custom-gallery-themes', themes => {
          return themes.filter(theme => theme.id !== id);
        });
      })
    );
  }

  private calcAutoCoverTitleColor(theme: GalleryTheme) {
    const darkBackground = isDarkColorFromString(theme.layout_page.background_color);
    const coverTitleDark = isDarkColorFromString(theme.cover.title_typography.font_color);
    const sectionTitleDark = isDarkColorFromString(theme.section.title_typography.font_color);
    if (theme.cover.design || darkBackground) {
      if (!coverTitleDark) return theme.cover.title_typography.font_color;
      if (!sectionTitleDark) return theme.section.title_typography.font_color;
      return '#FFFFFF';
    }

    if (coverTitleDark) return theme.cover.title_typography.font_color;
    if (sectionTitleDark) return theme.section.title_typography.font_color;
    return '#000000';
  }

  private calcAutoCoverDescriptionColor(theme: GalleryTheme) {
    const darkBackground = isDarkColorFromString(theme.layout_page.background_color);
    const coverParagraphDark = isDarkColorFromString(theme.cover.paragraph_typography.font_color);
    const sectionParagraphDark = isDarkColorFromString(theme.section.paragraph_typography.font_color);
    if (theme.cover.design || darkBackground) {
      if (!coverParagraphDark) return theme.cover.paragraph_typography.font_color;
      if (!sectionParagraphDark) return theme.section.paragraph_typography.font_color;
      return '#FFFFFF';
    }

    if (coverParagraphDark) return theme.cover.paragraph_typography.font_color;
    if (sectionParagraphDark) return theme.section.paragraph_typography.font_color;
    return '#000000';
  }

  private calcAutoSectionTitleColor(theme: GalleryTheme) {
    const darkBackground = isDarkColorFromString(theme.layout_page.background_color);
    const sectionTitleDark = isDarkColorFromString(theme.section.title_typography.font_color);
    if (darkBackground) {
      if (!sectionTitleDark) return theme.section.title_typography.font_color;
      return '#FFFFFF';
    }

    if (sectionTitleDark) return theme.section.title_typography.font_color;
    return '#000000';
  }

  private calcAutoSectionDescriptionColor(theme: GalleryTheme) {
    const darkBackground = isDarkColorFromString(theme.layout_page.background_color);
    const sectionParagraphDark = isDarkColorFromString(theme.section.paragraph_typography.font_color);
    if (darkBackground) {
      if (!sectionParagraphDark) return theme.section.paragraph_typography.font_color;
      return '#FFFFFF';
    }

    if (sectionParagraphDark) return theme.section.paragraph_typography.font_color;
    return '#000000';
  }

  public getCoverDesignChangeParams(theme: GalleryTheme, design: string | null) {
    const params: GalleryTheme = {
      ...theme,
      cover: {
        ...theme.cover,
        title_typography: {
          ...theme.cover.title_typography,
          font_color: this.calcAutoCoverTitleColor({ ...theme, cover: { ...theme.cover, design } }),
        },
        paragraph_typography: {
          ...theme.cover.paragraph_typography,
          font_color: this.calcAutoCoverDescriptionColor({ ...theme, cover: { ...theme.cover, design } }),
        },
        design,
      },
    };

    return params;
  }

  public getLayoutBackgroundChangeParams(theme: GalleryTheme, backgroundColor: string) {
    const newTheme = { ...theme, layout_page: { ...theme.layout_page, background_color: backgroundColor } };
    const params: GalleryTheme = {
      ...theme,
      cover: {
        ...theme.cover,
        title_typography: {
          ...theme.cover.title_typography,
          font_color: this.calcAutoCoverTitleColor(newTheme),
        },
        paragraph_typography: {
          ...theme.cover.paragraph_typography,
          font_color: this.calcAutoCoverDescriptionColor(newTheme),
        },
      },
      section: {
        ...theme.section,
        title_typography: {
          ...theme.section.title_typography,
          font_color: this.calcAutoSectionTitleColor(newTheme),
        },
        paragraph_typography: {
          ...theme.section.paragraph_typography,
          font_color: this.calcAutoSectionDescriptionColor(newTheme),
        },
      },
      layout_page: {
        ...theme.layout_page,
        background_color: backgroundColor,
      },
    };

    return params;
  }

  public getExtraFontFamilyUpdateParams(
    params: Partial<Typography>,
    currentValues: Typography,
    isTitle: boolean
  ): Observable<Partial<Typography>> {
    if (!params.font_family) return of(params);
    return forkJoin([
      this.googleFontService.getFontWeights(params.font_family).pipe(take(1)),
      this.googleFontService.hasItalic(params.font_family).pipe(take(1)),
    ]).pipe(
      map(([weights, italic]) => {
        if (currentValues.font_style === 'italic' && !italic) {
          params.font_style = 'normal';
        }

        if (!weights.includes(currentValues.font_weight)) {
          const defaultWeight = isTitle ? '700' : '400';
          params.font_weight = weights.includes(defaultWeight) ? defaultWeight : weights[0];
        }
        return params;
      })
    );
  }
}
